import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../tokens";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ── Brand constants ── */
const GREEN      = "#1e9254";
const GREEN_DARK = "#155c36";
const GREEN_LIGHT= "#7CDB9F"; // same light-green accent used for "Jirani" in the hero headline
const AMBER      = "#D97706";
const STAR_GOLD  = "#F0A93C";
const SERIF      = "'Instrument Serif', Georgia, serif";
const SANS       = tokens?.font?.family?.sans || "'DM Sans', -apple-system, sans-serif";
const BORDER     = "#E5E7EB";
const FAINT      = "#8a93a8";

/* Same dark radial gradient as HeroTopSection's background, so this
   section reads as a continuation of the hero rather than a new block. */
const HERO_BG = "radial-gradient(ellipse at 30% 15%, #1e4a2e 0%, #163322 45%, #0d2e1a 100%)";

if (typeof document !== "undefined" && !document.getElementById("__jirani_deals_fonts")) {
  const l = document.createElement("link");
  l.id = "__jirani_deals_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap";
  document.head.appendChild(l);
}

/* ── Image pools per category (swap for real pro photos from Supabase) ── */
const IMG = {
  fitness: [
    "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1594381898411-846e7d193883?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=340&h=280&fit=crop",
  ],
  hair: [
    "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1595475207225-428b62bda831?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1470259078422-826894b933aa?w=340&h=280&fit=crop",
  ],
  photography: [
    "https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1493863641943-9b68992a8d07?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1516724562728-afc824a36e84?w=340&h=280&fit=crop",
  ],
};

const LOCATIONS = [
  "Nyali", "Westlands", "Bamburi", "Kilimani", "Tudor", "Kasarani",
  "Likoni", "Embakasi", "Mtwapa", "Lang'ata", "Shanzu", "South B",
  "Nakuru", "Kisumu", "Naivasha", "Milimani", "Thika", "Juja", "Meru", "Eldoret",
];

/* Three fresh, non-overlapping name pools — checked against every name
   already used on the homepage (hero strip, testimonials, GENERAL_NAMES,
   RAIN_NAMES, and the Childcare/Tutoring/Chef pools in ForYourHousehold),
   and against each other. */
const FITNESS_NAMES = ["Derrick", "Faridah", "Norah", "Kiptoo", "Janet", "Mwangi", "Abigael", "Chege", "Rispah", "Nixon",
  "Carol", "Maina", "Evelyn", "Mutiso", "Beryl", "Kiprono", "Lorna", "Ken", "Tabitha", "Njoroge"];

const BEAUTY_NAMES = ["Sheila", "Barasa", "Mumbi", "Kiptum", "Joan", "Wafula", "Achieng", "Mutai", "Nafula", "Simiyu",
  "Belinda", "Otiende", "Wangari", "Kioko", "Atieno", "Cherotich", "Jecinta", "Njogu", "Auma", "Kiplangat"];

const PHOTOGRAPHY_NAMES = ["Anita", "Odhiambo", "Naliaka", "Kiragu", "Wekesa", "Nyabuti", "Chelangat", "Miano", "Mutugi", "Wanjala",
  "Akinyi", "Kipchumba", "Nekesa", "Mutindi", "Karanja", "Wairimu", "Onsongo", "Muthini", "Cherono", "Lugard"];

function makeName(namePool, i) {
  return `${namePool[i % namePool.length]} ${String.fromCharCode(65 + (i % 26))}.`;
}

/* Same controlled jobs-count distribution used in JiraniDeals.jsx and
   ForYourHousehold.jsx: 3 Beginner, 10 Junior, 5 Senior, 2 Business Pro —
   most values sit under 70. Rotated per row so rows don't repeat numbers. */
const JOBS_PATTERN = [8, 14, 19, 27, 31, 35, 38, 41, 44, 46, 29, 33, 48, 52, 58, 61, 65, 68, 110, 130];

function buildRow(categoryKey, categoryLabel, count, priceRange, namePool, idOffset) {
  const rotate = Math.floor(idOffset / 100);
  return Array.from({ length: count }, (_, i) => {
    const discount = [10, 12, 15, 18, 20, 25, 30][i % 7];
    const original = priceRange[0] + ((i * 137) % (priceRange[1] - priceRange[0]));
    const roundedOriginal = Math.round(original / 50) * 50;
    const current = Math.round((roundedOriginal * (1 - discount / 100)) / 10) * 10;
    return {
      id: idOffset + i + 1,
      name: makeName(namePool, i),
      category: categoryLabel,
      location: LOCATIONS[i % LOCATIONS.length],
      image: IMG[categoryKey][i % IMG[categoryKey].length],
      discount,
      original: roundedOriginal,
      current,
      rating: (4.6 + ((i * 7) % 5) / 10).toFixed(1),
      jobs: JOBS_PATTERN[(i + rotate) % JOBS_PATTERN.length],
    };
  });
}

const FITNESS_DEALS     = buildRow("fitness", "Fitness Coaching", 20, [300, 700], FITNESS_NAMES, 500);
const BEAUTY_DEALS      = buildRow("hair", "Hair & Beauty", 20, [300, 700], BEAUTY_NAMES, 600);
const PHOTOGRAPHY_DEALS = buildRow("photography", "Photography", 20, [350, 750], PHOTOGRAPHY_NAMES, 700);

function fmt(n) {
  return `KSh ${n.toLocaleString("en-KE")}`;
}

/* Level bands are based on jobs completed:
   Beginner < 25, Junior 25-49, Senior 50-99, Business Pro 100+ */
const LEVELS = {
  beginner:    { label: "Beginner",     bg: "#F1F2F5", color: "#4a5268", border: "#E5E7EB", star: false },
  junior:      { label: "Junior",       bg: "#EAF1FB", color: "#2563AA", border: "#BFDBFE", star: false },
  senior:      { label: "Senior",       bg: "#eef8f3", color: GREEN_DARK, border: "#a7d9bc", star: false },
  businessPro: { label: "Business Pro", bg: AMBER,     color: "#fff",    border: AMBER,     star: true  },
};

function getLevel(jobs) {
  if (jobs >= 100) return LEVELS.businessPro;
  if (jobs >= 50) return LEVELS.senior;
  if (jobs >= 25) return LEVELS.junior;
  return LEVELS.beginner;
}

function Avatar({ src, name, style }) {
  const [err, setErr] = useState(false);
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (err || !src) {
    return (
      <div style={{ ...style, background: "#eef8f3", display: "flex", alignItems: "center", justifyContent: "center", color: GREEN_DARK, fontWeight: 700, fontFamily: SANS }}>
        {initials}
      </div>
    );
  }
  return <img src={src} alt={name} style={style} onError={() => setErr(true)} />;
}

/* Card itself stays white — it sits on the dark section background, so
   contrast and legibility don't change from the light-background rows. */
function DealCard({ deal, onBook }) {
  const [hovered, setHovered] = useState(false);
  const level = getLevel(deal.jobs);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onBook(deal)}
      style={{
        width: 158, flexShrink: 0, borderRadius: 14, background: "#fff",
        border: `1px solid ${hovered ? "#a7d9bc" : BORDER}`,
        boxShadow: hovered ? "0 10px 26px rgba(0,0,0,.22)" : "0 4px 16px rgba(0,0,0,.14)",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all .18s ease", overflow: "hidden", cursor: "pointer",
      }}
    >
      <div style={{ position: "relative", height: 120, background: "#eef1ee" }}>
        <Avatar src={deal.image} name={deal.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{ position: "absolute", top: 7, left: 7, background: "#fff", borderRadius: 6, padding: "3px 7px", fontSize: 9.5, fontWeight: 500, color: GREEN_DARK, border: "1px solid #a7d9bc", fontFamily: SANS }}>
          Verified
        </div>
        <div style={{ position: "absolute", top: 7, right: 7, background: AMBER, borderRadius: 6, padding: "3px 7px", fontSize: 9.5, fontWeight: 500, color: "#fff", fontFamily: SANS }}>
          {deal.discount}% off
        </div>
      </div>
      <div style={{ padding: "9px 10px 11px" }}>
        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 500, color: "#0f1117", fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {deal.name}
        </p>
        <p style={{ margin: "2px 0 0", fontSize: 10.5, color: FAINT, fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {deal.category} · {deal.location}
        </p>
        <div style={{ margin: "5px 0 0" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            fontSize: 9, fontWeight: 600, color: level.color,
            background: level.bg, border: `1px solid ${level.border}`,
            borderRadius: 5, padding: "2px 6px", fontFamily: SANS,
          }}>
            {level.star && (
              <svg width="7" height="7" viewBox="0 0 20 20" fill="#fff" style={{ flexShrink: 0 }}>
                <path d="M10 1.2l2.7 5.7 6.2.6-4.6 4.2 1.3 6.1L10 14.6l-5.6 3.2 1.3-6.1L1.1 7.5l6.2-.6z" />
              </svg>
            )}
            {level.label}
          </span>
        </div>
        <div style={{ margin: "5px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="10" height="10" viewBox="0 0 20 20" fill={STAR_GOLD} style={{ flexShrink: 0 }}>
            <path d="M10 1.2l2.7 5.7 6.2.6-4.6 4.2 1.3 6.1L10 14.6l-5.6 3.2 1.3-6.1L1.1 7.5l6.2-.6z" />
          </svg>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: "#B8791A", fontFamily: SANS }}>{deal.rating}</span>
          <span style={{ fontSize: 10, color: FAINT, fontFamily: SANS }}>·</span>
          <span style={{ fontSize: 10.5, fontWeight: 600, color: GREEN_DARK, fontFamily: SANS }}>{deal.jobs} jobs</span>
        </div>
        <div style={{ margin: "6px 0 0", display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f1117", fontFamily: SANS }}>From {fmt(deal.current)}</span>
          <span style={{ fontSize: 11, color: "#9CA3AF", textDecoration: "line-through", fontFamily: SANS }}>{fmt(deal.original)}</span>
        </div>
      </div>
    </div>
  );
}

/* Book button — solid green so it stays visible even against the dark
   section background; inverts to an outlined confirmed look once booked. */
function BookButton({ deal, inCart, onToggleCart }) {
  return (
    <button
      onClick={() => onToggleCart(deal)}
      style={{
        marginTop: 8, width: 158, height: 32, borderRadius: 8,
        border: `1.5px solid ${GREEN}`,
        background: inCart ? "#fff" : GREEN,
        color: inCart ? GREEN_DARK : "#fff",
        fontFamily: SERIF, fontSize: 13.5, fontWeight: 500,
        cursor: "pointer", transition: "all .15s ease", flexShrink: 0,
        boxShadow: inCart ? "none" : "0 3px 10px rgba(0,0,0,.25)",
      }}
    >
      {inCart ? "Booked ✓" : "Book"}
    </button>
  );
}

/* Arrow buttons — translucent-white variant matching the hero's own
   scroll arrows, since this row sits on the same dark background. */
function arrowStyleDark(enabled) {
  return {
    width: 32, height: 32, borderRadius: "50%",
    border: "1.5px solid rgba(255,255,255,0.38)",
    background: "rgba(255,255,255,0.14)",
    backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
    fontSize: 16, color: "#fff", cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.3, lineHeight: 1,
  };
}

/* ── Row — header sits in a padded column, track is full width and
       bleeds past it on the right, same pattern as the other carousels. ── */
function DealsRow({ title, deals, onBook, cartIds, onToggleCart, padX, marginBottom = 30 }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    update();
    return () => el.removeEventListener("scroll", update);
  }, [update, deals]);

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 480, behavior: "smooth" });

  return (
    <div style={{ marginBottom }}>
      <div style={{ width: "100%", padding: `0 ${padX}px` }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 18, gap: 16 }}>
          <h3 style={{ fontFamily: SERIF, fontSize: "clamp(24px,3.2vw,32px)", fontWeight: 400, color: "#fff", lineHeight: 1.08, letterSpacing: "-0.03em", margin: 0 }}>
            {title}
          </h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => scroll(-1)} disabled={!canLeft} style={arrowStyleDark(canLeft)} aria-label="Scroll left">‹</button>
            <button onClick={() => scroll(1)} disabled={!canRight} style={arrowStyleDark(canRight)} aria-label="Scroll right">›</button>
          </div>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="ytc-track"
        style={{ overflowX: "auto", paddingBottom: 4, paddingLeft: padX, paddingRight: padX, width: "100%" }}
      >
        <div style={{ display: "flex", gap: 12, width: "max-content" }}>
          {deals.map(deal => (
            <div key={deal.id} style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
              <DealCard deal={deal} onBook={onBook} />
              <BookButton deal={deal} inCart={cartIds.has(deal.id)} onToggleCart={onToggleCart} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TreatYourself() {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const padX = isMobile ? 16 : isTablet ? 20 : 32;
  const onBook = useCallback((deal) => navigate(`/pro/${deal.id}?book=1&promo=1`), [navigate]);

  // Standalone cart state for now, same pattern as the other rows — swap
  // for your real cart context/store later; onToggleCart is the only
  // thing that needs rewiring.
  const [cartIds, setCartIds] = useState(() => new Set());
  const onToggleCart = useCallback((deal) => {
    setCartIds(prev => {
      const next = new Set(prev);
      if (next.has(deal.id)) next.delete(deal.id);
      else next.add(deal.id);
      return next;
    });
  }, []);

  return (
    <section style={{ background: HERO_BG, padding: isMobile ? "36px 0 40px" : "48px 0 52px", width: "100%", overflow: "hidden" }}>
      <style>{`.ytc-track::-webkit-scrollbar{display:none}.ytc-track{-ms-overflow-style:none;scrollbar-width:none}`}</style>

      {/* Section header — sits in the same padded column as each row's own header */}
      <div style={{ width: "100%", padding: `0 ${padX}px`, marginBottom: 4 }}>
        <h2 style={{
          fontFamily: SERIF, fontWeight: 400, color: "#fff",
          fontSize: "clamp(30px,4.4vw,42px)", lineHeight: 1.05,
          letterSpacing: "-0.03em", margin: "0 0 20px",
        }}>
          Treat <em style={{ fontStyle: "italic", color: GREEN_LIGHT }}>yourself</em>, for once
        </h2>
      </div>

      <DealsRow
        title="Fitness Coaching"
        deals={FITNESS_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX}
      />
      <DealsRow
        title="Hair & Beauty"
        deals={BEAUTY_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX}
      />
      <DealsRow
        title="Photography"
        deals={PHOTOGRAPHY_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX} marginBottom={0}
      />
    </section>
  );
}